import React, {createContext, useContext, useEffect, useState, ReactNode} from "react";
import authService from "@/services/authService";
import {ucApi, AuthMethod} from "@/services/api/uc";
import { ResponseData } from "@/utils/axios";
import { clearAuthState, getAuthorizationCodeFromUrl, getDeviceInfo, normalizeAuthMethod } from "@/utils/auth";
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { intercomService } from "@/services/intercom";

// Define the User type with necessary properties
export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  nickname?: string;
  role: "user" | "author" | "store_owner" | "admin";
  provider?: "email" | "google" | "apple" | "facebook";
  subjectIdentityId?: string;
  identityId?: string;
  identityScope?: string;
  details?: any;
}

// Define the auth context type
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;
  login: (email: string, password: string, identityScope?: string, authMethod?: string) => Promise<ResponseData>;
  loginWithOtp: (email: string, otp: string, password: string) => Promise<ResponseData>;
  loginWithCode: (email: string, code: string, password?: string) => Promise<ResponseData>;
  sendOtp: (email: string, type?: 'login' | 'register' | 'forget' | 'personal') => Promise<void>;
  loginWithProvider: (provider: "google" | "apple" | "facebook") => Promise<void>;
  loginWithThirdParty: (provider: "google" | "apple", authCode: string, idToken?: string, redirectUri?: string) => Promise<void>;
  register: (name: string, email: string, password: string, verificationCode: string) => Promise<void>;
  logout: () => void;
  resetPassword: (email: string) => Promise<void>;
  checkEmailOtpSettings: (email: string) => Promise<any>;
  checkEmailExists: (email: string) => Promise<boolean>;
  checkEmailLegality: (email: string) => Promise<boolean>;
  checkLastLogoff: (email: string) => Promise<ResponseData>;
  verifyCode: (email: string, code: string, type: 'login' | 'register' | 'forget' | 'personal') => Promise<boolean>;
  resetPasswordWithCode: (email: string, code: string, password: string) => Promise<boolean>;
}

// Create the auth context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: false,
  isAuthenticated: false,
  setUser: () => {},
  login: async () => ({ code: 1000, message: "Success", data: null }),
  loginWithOtp: async () => ({ code: 1000, message: "Success", data: null }),
  loginWithCode: async () => ({ code: 1000, message: "Success", data: null }),
  sendOtp: async () => {},
  logout: () => {},
  loginWithProvider: async () => {},
  loginWithThirdParty: async () => {},
  register: async () => {},
  resetPassword: async () => {},
  checkEmailOtpSettings: async () => {},
  checkEmailExists: async () => false,
  checkEmailLegality: async () => false,
  checkLastLogoff: async () => ({ code: 1000, message: "Success", data: null }),
  verifyCode: async () => false,
  resetPasswordWithCode: async () => false,
});

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);

  // 如果没有在AuthProvider内部使用这个钩子，返回默认值而不是抛出错误
  if (!context) {
    console.warn("useAuth must be used within an AuthProvider");
    // 返回默认值，防止应用崩溃
    return {
      user: null,
      isLoading: false,
      isAuthenticated: false,
      setUser: () => {},
      login: async () => ({ code: 1000, message: "Success", data: null }),
      loginWithOtp: async () => ({ code: 1000, message: "Success", data: null }),
      loginWithCode: async () => ({ code: 1000, message: "Success", data: null }),
      sendOtp: async () => {},
      logout: () => {},
      loginWithProvider: async () => {},
      loginWithThirdParty: async () => {},
      register: async () => {},
      resetPassword: async () => {},
      checkEmailOtpSettings: async () => {},
      checkEmailExists: async () => false,
      checkEmailLegality: async () => false,
      checkLastLogoff: async () => ({ code: 1000, message: "Success", data: null }),
      verifyCode: async () => false,
      resetPasswordWithCode: async () => false,
    };
  }

  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const navigate = useNavigate();

  // Initialize Intercom when user state changes
  useEffect(() => {
    if (user && isAuthenticated) {
      intercomService.initForLoggedInUser({
        user_id: user.id,
        name: user.nickname || user.name,
        email: user.email,
        created_at: user.details?.subject?.createTime ? Math.floor(new Date(user.details.subject.createTime).getTime() / 1000) : Math.floor(Date.now() / 1000)
      });
    } else {
      intercomService.initForAnonymousUser();
    }

    return () => {
      intercomService.shutdown();
    };
  }, [user, isAuthenticated]);

  // Check for existing user session on initial load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // 从localStorage或cookie中获取token
        const token = document.cookie.split('; ').find(row => row.startsWith('anzir_token='))?.split('=')[1];
        const userData = localStorage.getItem("anzir_user");

        if (token && userData) {
          // 如果有token和用户数据，则认为用户已登录
          const parsedUserData = JSON.parse(userData);

          // 检查头像URL是否包含基础路径
          if (parsedUserData.avatar &&
              !parsedUserData.avatar.startsWith('http') &&
              !parsedUserData.avatar.includes(import.meta.env.VITE_STATIC_URL) &&
              parsedUserData.details?.subject?.icon) {
            parsedUserData.avatar = `${import.meta.env.VITE_STATIC_URL}${parsedUserData.details.subject.icon}`;
          }

          // 如果没有头像，设置默认头像
          if (!parsedUserData.avatar) {
            parsedUserData.avatar = import.meta.env.VITE_APP_BASE_HEAD;
          }

          setUser(parsedUserData);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // 检查邮箱是否存在
  const checkEmailExists = async (email: string): Promise<boolean> => {
    try {
      const response = await ucApi.auth.hasEmail({ email });
      return response.code === 1000 && response.data === true;
    } catch (error) {
      console.error("Error checking email:", error);
      return false;
    }
  };

  // 检查邮箱OTP设置
  const checkEmailOtpSettings = async (email: string): Promise<any> => {
    try {
      return await ucApi.auth.emailAuth({email});
    } catch (error) {
      console.error("Error checking email OTP settings:", error);
      throw error;
    }
  };

  // 检查邮箱是否合规
  const checkEmailLegality = async (email: string): Promise<boolean> => {
    try {
      const response = await ucApi.auth.emailLegal({ email });
      return response.code === 1000;
    } catch (error) {
      console.error("Error checking email legality:", error);
      return false;
    }
  };

  // 使用新的真实登录API替换mock登录
  const login = async (email: string, password: string, identityScope?: string, authMethod?: string) => {
    setIsLoading(true);

    try {
      // 首先验证邮箱是否存在
      const emailExists = await checkEmailExists(email);
      if (!emailExists) {
        throw new Error("Email does not exist");
      }

      // 创建设备信息对象
      const deviceInfo = {
        deviceId: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
        deviceType: "web",
        appVersion: import.meta.env.VITE_APP_VERSION || "1.24.0"
      };

      // 确定当前authMethod
      const currentAuthMethod = authMethod ? (authMethod as AuthMethod) : AuthMethod.EmailPwd;

      // 构建登录参数
      const loginParams = {
        certificateIdentification: email, // 使用字符串形式的邮箱
        certificate: currentAuthMethod === AuthMethod.EmailPwdOtp ?
          { optCode: "", pwd: password } : // 如果是OTP登录，使用带optCode的对象形式
          password, // 普通登录直接使用密码字符串
        device: deviceInfo,
        identityScope: identityScope || import.meta.env.VITE_APP_LOGIN_SCOPE || "player",
        authMethod: currentAuthMethod
      };

      // 发送登录请求
      const loginResponse = await ucApi.auth.login(loginParams);
      console.log("Login response:", loginResponse);

      // 登录成功，保存token
      if (loginResponse.data.token) {
        document.cookie = `anzir_token=Anzir ${loginResponse.data.token}; path=/; max-age=${loginResponse.data.expiresIn}`;
      }

      // 保存refreshToken到localStorage
      if (loginResponse.data.refreshToken) {
        localStorage.setItem("anzir_refresh_token", loginResponse.data.refreshToken);
      }

      try {
        // 获取用户详细信息
        const userDetailResponse = await ucApi.auth.getUserDetail();
        console.log("User detail:", userDetailResponse.data);

        // 提取关键用户信息
        const userDetail = userDetailResponse.data;
        const userSubject = userDetail.details?.subject;

        // 构建用户对象，包含必要字段
        const userData: User = {
          id: userDetail.subjectIdentityId || "",
          name: userDetail.username || "",
          email: userDetail.details?.loginAccount?.email || "",
          nickname: userSubject?.nickname || userDetail.username || "",
          avatar: userSubject?.icon ? `${import.meta.env.VITE_STATIC_URL}${userSubject.icon}` : import.meta.env.VITE_APP_BASE_HEAD,
          role: "user",
          provider: "email",
          subjectIdentityId: userDetail.subjectIdentityId,
          identityId: userDetail.identityId,
          identityScope: userDetail.identityScope,
          details: userDetail.details
        };

        // 保存完整的用户信息到localStorage
        localStorage.setItem("anzir_user", JSON.stringify(userData));

        // 更新Context中的用户状态
        setUser(userData);
        setIsAuthenticated(true);
      } catch (detailError) {
        console.error("Error fetching user details:", detailError);
        // 详情获取失败，使用登录返回的基本用户信息
        if (loginResponse.data.user) {
          // 构建基本用户信息
          const basicUserData: User = {
            id: loginResponse.data.user.subjectIdentityId || "",
            name: loginResponse.data.user.username || "",
            email: loginResponse.data.user.email || "",
            role: "user",
            provider: "email"
          };

          localStorage.setItem("anzir_user", JSON.stringify(basicUserData));
          setUser(basicUserData);
          setIsAuthenticated(true);
        }
      }

      setIsLoading(false);
      return loginResponse;
    } catch (error: any) {
      setIsLoading(false);
      console.error("Login error:", error);
      throw new Error(error.message || "Login failed");
    }
  };

  // 发送验证码
  const sendOtp = async (email: string, type: 'login' | 'register' | 'forget' | 'personal' = 'login'): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await ucApi.auth.codeSend({
        email,
        name: email,
        type
      });

      if (response.code !== 1000) {
        throw new Error(response.message || "Failed to send verification code");
      }
    } catch (error) {
      console.error("Send OTP error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // OTP验证登录
  const loginWithOtp = async (email: string, otp: string, password: string): Promise<ResponseData> => {
    setIsLoading(true);

    try {
      // 创建设备信息对象
      const deviceInfo = {
        deviceId: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
        deviceType: "web",
        appVersion: import.meta.env.VITE_APP_VERSION || "1.24.0"
      };

      // 构建OTP登录参数
      const otpLoginParams = {
        certificateIdentification: email, // 使用字符串形式的邮箱
        certificate: {
          optCode: otp,
          pwd: password
        },
        device: deviceInfo,
        identityScope: import.meta.env.VITE_APP_LOGIN_SCOPE || "player",
        authMethod: AuthMethod.EmailPwdOtp
      };

      // 发送OTP登录请求
      const loginResponse = await ucApi.auth.login(otpLoginParams);
      console.log("OTP Login response:", loginResponse);

      if (loginResponse.code !== 1000) {
        throw new Error(loginResponse.message || "The code is incorrect");
      }

      // 登录成功，保存token
      if (loginResponse.data.token) {
        document.cookie = `anzir_token=Anzir ${loginResponse.data.token}; path=/; max-age=${loginResponse.data.expiresIn}`;
      }

      // 保存refreshToken到localStorage
      if (loginResponse.data.refreshToken) {
        localStorage.setItem("anzir_refresh_token", loginResponse.data.refreshToken);
      }

      try {
        // 获取用户详细信息
        const userDetailResponse = await ucApi.auth.getUserDetail();
        console.log("User detail (OTP):", userDetailResponse.data);

        // 提取关键用户信息
        const userDetail = userDetailResponse.data;
        const userSubject = userDetail.details?.subject;

        // 构建用户对象，包含必要字段
        const userData: User = {
          id: userDetail.subjectIdentityId || "",
          name: userDetail.username || "",
          email: userDetail.details?.loginAccount?.email || "",
          nickname: userSubject?.nickname || userDetail.username || "",
          avatar: userSubject?.icon ? `${import.meta.env.VITE_STATIC_URL}${userSubject.icon}` : import.meta.env.VITE_APP_BASE_HEAD,
          role: "user",
          provider: "email",
          subjectIdentityId: userDetail.subjectIdentityId,
          identityId: userDetail.identityId,
          identityScope: userDetail.identityScope,
          details: userDetail.details
        };

        // 保存完整的用户信息到localStorage
        localStorage.setItem("anzir_user", JSON.stringify(userData));

        // 更新Context中的用户状态
        setUser(userData);
        setIsAuthenticated(true);
      } catch (detailError) {
        console.error("Error fetching user details after OTP login:", detailError);
        // 详情获取失败，使用登录返回的基本用户信息
        if (loginResponse.data.user) {
          const basicUserData: User = {
            id: loginResponse.data.user.subjectIdentityId || "",
            name: loginResponse.data.user.username || "",
            email: loginResponse.data.user.email || "",
            role: "user",
            provider: "email"
          };

          localStorage.setItem("anzir_user", JSON.stringify(basicUserData));
          setUser(basicUserData);
          setIsAuthenticated(true);
        }
      }

      setIsLoading(false);
      return loginResponse;
    } catch (error: any) {
      setIsLoading(false);
      console.error("OTP login error:", error);
      throw new Error(error.message || "The code is incorrect");
    }
  };

  // 验证码登录
  const loginWithCode = async (email: string, code: string, password?: string): Promise<ResponseData> => {
    setIsLoading(true);

    try {
      // 检查参数
      if (!email || !code) {
        throw new Error("Email and verification code are required");
      }
      
      console.log("Login with code:", { email, code, passwordProvided: !!password });

      // 创建设备信息对象
      const deviceInfo = {
        deviceId: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
        deviceType: "web",
        appVersion: import.meta.env.VITE_APP_VERSION || "1.24.0"
      };

      // 构建登录参数
      const otpLoginParams = {
        certificateIdentification: email, // 使用字符串形式的邮箱
        certificate: code,
        device: deviceInfo,
        identityScope: import.meta.env.VITE_APP_LOGIN_SCOPE || "player",
        authMethod: AuthMethod.EmailCode
      };

      console.log("Code login params:", otpLoginParams);

      // 发送登录请求
      const loginResponse = await ucApi.auth.login(otpLoginParams);
      console.log("Code Login response:", loginResponse);

      if (loginResponse.code !== 1000) {
        throw new Error(loginResponse.message || "The code is incorrect");
      }

      // 登录成功，保存邮箱到localStorage
      localStorage.setItem('anzir_lastEmail', email);

      // 登录成功，保存token
      if (loginResponse.data.token) {
        document.cookie = `anzir_token=Anzir ${loginResponse.data.token}; path=/; max-age=${loginResponse.data.expiresIn}`;
      }

      // 保存refreshToken到localStorage
      if (loginResponse.data.refreshToken) {
        localStorage.setItem("anzir_refresh_token", loginResponse.data.refreshToken);
      }

      try {
        // 获取用户详细信息
        const userDetailResponse = await ucApi.auth.getUserDetail();
        console.log("User detail (OTP):", userDetailResponse.data);

        // 提取关键用户信息
        const userDetail = userDetailResponse.data;
        const userSubject = userDetail.details?.subject;

        // 构建用户对象，包含必要字段
        const userData: User = {
          id: userDetail.subjectIdentityId || "",
          name: userDetail.username || "",
          email: userDetail.details?.loginAccount?.email || "",
          nickname: userSubject?.nickname || userDetail.username || "",
          avatar: userSubject?.icon ? `${import.meta.env.VITE_STATIC_URL}${userSubject.icon}` : import.meta.env.VITE_APP_BASE_HEAD,
          role: "user",
          provider: "email",
          subjectIdentityId: userDetail.subjectIdentityId,
          identityId: userDetail.identityId,
          identityScope: userDetail.identityScope,
          details: userDetail.details
        };

        // 保存完整的用户信息到localStorage
        localStorage.setItem("anzir_user", JSON.stringify(userData));

        // 更新Context中的用户状态
        setUser(userData);
        setIsAuthenticated(true);
      } catch (detailError) {
        console.error("Error fetching user details after OTP login:", detailError);
        // 详情获取失败，使用登录返回的基本用户信息
        if (loginResponse.data.user) {
          const basicUserData: User = {
            id: loginResponse.data.user.subjectIdentityId || "",
            name: loginResponse.data.user.username || "",
            email: loginResponse.data.user.email || "",
            role: "user",
            provider: "email"
          };

          localStorage.setItem("anzir_user", JSON.stringify(basicUserData));
          setUser(basicUserData);
          setIsAuthenticated(true);
        }
      }

      setIsLoading(false);
      return loginResponse;
    } catch (error: any) {
      setIsLoading(false);
      console.error("OTP login error:", error);
      throw new Error(error.message || "The code is incorrect");
    }
  };

  // Register with email and password
  const register = async (name: string, email: string, password: string, verificationCode: string): Promise<void> => {
    setIsLoading(true);

    try {
      // 创建设备信息对象
      const deviceInfo = {
        deviceId: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
        deviceType: "web",
        appVersion: import.meta.env.VITE_APP_VERSION || "1.24.0"
      };

      // 构建注册参数
      const registerParams = {
        account: email,
        pwd: password,
        verificationCode: verificationCode,
        authMethod: "EMAIL_CODE",
        scope: import.meta.env.VITE_APP_LOGIN_SCOPE || "player",
        details: {
          nickname: name,
          sex: null,
          email: email,
          birthday: null,
          password: password,
          icon: "static/heads/1.png" // 默认头像
        },
        device: deviceInfo
      };

      // 首先进行注册合法性检查
      const checkResponse = await ucApi.auth.checkRegister(registerParams);
      if (checkResponse.code !== 1000) {
        // 检查是否是验证码错误 (40001)
        if (checkResponse.code === 40001) {
          const error = new Error(checkResponse.message || "Verification code is incorrect");
          // @ts-ignore - 添加自定义code属性
          error.code = checkResponse.code;
          throw error;
        }
        throw new Error(checkResponse.message || "Registration check failed");
      }

      // 合法性检查通过，进行正式注册
      const registerResponse = await ucApi.auth.register(registerParams);
      if (registerResponse.code !== 1000) {
        // 检查是否是验证码错误 (40001)
        if (registerResponse.code === 40001) {
          const error = new Error(registerResponse.message || "Verification code is incorrect");
          // @ts-ignore - 添加自定义code属性
          error.code = registerResponse.code;
          throw error;
        }
        throw new Error(registerResponse.message || "Registration failed");
      }

      // 注册成功后处理
      if (registerResponse.data && registerResponse.data.token) {
        document.cookie = `anzir_token=Anzir ${registerResponse.data.token}; path=/; max-age=${registerResponse.data.expiresIn}`;
        
        // 保存refreshToken到localStorage
        if (registerResponse.data.refreshToken) {
          localStorage.setItem("anzir_refresh_token", registerResponse.data.refreshToken);
        }

        try {
          // 获取用户详细信息
          const userDetailResponse = await ucApi.auth.getUserDetail();
          console.log("User detail after registration:", userDetailResponse.data);

          // 提取关键用户信息
          const userDetail = userDetailResponse.data;
          const userSubject = userDetail.details?.subject;

          // 构建用户对象，包含必要字段
          const userData: User = {
            id: userDetail.subjectIdentityId || "",
            name: userDetail.username || name,
            email: userDetail.details?.loginAccount?.email || email,
            nickname: userSubject?.nickname || userDetail.username || name,
            avatar: userSubject?.icon ? `${import.meta.env.VITE_STATIC_URL}${userSubject.icon}` : import.meta.env.VITE_APP_BASE_HEAD,
            role: "user",
            provider: "email",
            subjectIdentityId: userDetail.subjectIdentityId,
            identityId: userDetail.identityId,
            identityScope: userDetail.identityScope,
            details: userDetail.details
          };

          // 保存用户邮箱到localStorage供登录表单使用
          localStorage.setItem('anzir_lastEmail', email);

          // 保存完整的用户信息到localStorage
          localStorage.setItem("anzir_user", JSON.stringify(userData));

          // 更新Context中的用户状态
          setUser(userData);
          setIsAuthenticated(true);
        } catch (detailError) {
          console.error("Error fetching user details after registration:", detailError);
          
          // 保存用户邮箱到localStorage供登录表单使用
          localStorage.setItem('anzir_lastEmail', email);

          // 详情获取失败，使用注册返回的基本用户信息
          const responseUser = registerResponse.data.user;
          const basicUserData: User = {
            id: responseUser.id || "",
            name: responseUser.name || name,
            email: email, // 使用注册邮箱，因为API返回的email可能为null
            nickname: responseUser.nickname || name,
            avatar: responseUser.avatarUrl || import.meta.env.VITE_APP_BASE_HEAD,
            role: "user",
            provider: "email",
            subjectIdentityId: responseUser.id
          };

          localStorage.setItem("anzir_user", JSON.stringify(basicUserData));
          setUser(basicUserData);
          setIsAuthenticated(true);
        }
      } else {
        // 如果没有token，可能需要额外的登录步骤
        throw new Error("Registration successful but no token received. Please login.");
      }
    } catch (error) {
      console.error("Registration error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Third-party provider login
  const loginWithProvider = async (provider: "google" | "apple" | "facebook"): Promise<void> => {
    setIsLoading(true);

    try {
      // 获取授权码
      const code = getAuthorizationCodeFromUrl();
      if (!code) {
        throw new Error(`No authorization code found for ${provider} login`);
      }

      // 获取设备信息
      const deviceInfo = getDeviceInfo();

      // 获取当前URL作为redirectUri (去除查询参数)
      const redirectUri = `${window.location.origin}${window.location.pathname}`;

      // 确定认证方法
      let authMethod;
      if (provider === "google") {
        authMethod = AuthMethod.GoogleAuthCodeFlow;
      } else if (provider === "apple") {
        authMethod = AuthMethod.AppleAuthCodeFlow;
      } else {
        throw new Error(`${provider} login is not supported yet`);
      }

      // 构建三方登录请求参数
      const thirdPartyLoginParams = {
        authMethod,
        identityScope: import.meta.env.VITE_APP_LOGIN_SCOPE || "player",
        authorization: {
          code,
          redirectUri
        },
        bizType: ["AccountNotExistRegister", "Login"],
        device: deviceInfo
      };

      console.log('Third party login params:', thirdPartyLoginParams);

      // 调用三方登录API
      const loginResponse = await ucApi.auth.thirdPartyLogin(thirdPartyLoginParams);

      if (loginResponse.code !== 1000 || !loginResponse.data) {
        throw new Error(loginResponse.message || `${provider} login failed`);
      }

      // 登录成功，保存token
      if (loginResponse.data.token) {
        document.cookie = `anzir_token=Anzir ${loginResponse.data.token}; path=/; max-age=${loginResponse.data.expiresIn || 3600}`;
      }

      // 保存refreshToken到localStorage
      if (loginResponse.data.refreshToken) {
        localStorage.setItem("anzir_refresh_token", loginResponse.data.refreshToken);
      }

      // 处理用户信息
      if (loginResponse.data.user) {
        const userData: User = {
          id: loginResponse.data.user.id || "",
          name: loginResponse.data.user.name || "",
          email: loginResponse.data.user.email || "",
          nickname: loginResponse.data.user.nickname || loginResponse.data.user.name || "",
          avatar: loginResponse.data.user.avatarUrl || import.meta.env.VITE_APP_BASE_HEAD,
          role: "user",
          provider: provider
        };

        // 保存用户信息
        localStorage.setItem("anzir_user", JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        // 如果没有用户信息，尝试获取用户详情
        try {
          const userDetailResponse = await ucApi.auth.getUserDetail();
          console.log("User detail after third-party login:", userDetailResponse.data);

          // 提取关键用户信息
          const userDetail = userDetailResponse.data;
          const userSubject = userDetail.details?.subject;

          // 构建用户对象，包含必要字段
          const userData: User = {
            id: userDetail.subjectIdentityId || "",
            name: userDetail.username || "",
            email: userDetail.details?.loginAccount?.email || "",
            nickname: userSubject?.nickname || userDetail.username || "",
            avatar: userSubject?.icon ? `${import.meta.env.VITE_STATIC_URL}${userSubject.icon}` : import.meta.env.VITE_APP_BASE_HEAD,
            role: "user",
            provider: provider,
            subjectIdentityId: userDetail.subjectIdentityId,
            identityId: userDetail.identityId,
            identityScope: userDetail.identityScope,
            details: userDetail.details
          };

          // 保存完整的用户信息到localStorage
          localStorage.setItem("anzir_user", JSON.stringify(userData));

          // 更新Context中的用户状态
          setUser(userData);
          setIsAuthenticated(true);
        } catch (detailError) {
          console.error("Error fetching user details after third-party login:", detailError);
          throw new Error("Login successful but failed to get user details");
        }
      }

      // 清除URL参数中的code
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);

      // 成功后处理重定向
      const redirectTo = sessionStorage.getItem('authRedirectTo') || '/profile';
      sessionStorage.removeItem('authRedirectTo');
      
      // 显示成功消息
      toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} Login Successful`, {
        description: 'You have successfully logged in.',
      });
      
      // 如果当前不是在回调页面上，则导航到重定向页面
      if (!window.location.pathname.includes('/auth/') && !window.location.pathname.includes('/callback')) {
        navigate(redirectTo, { replace: true });
      }
      
      return;
    } catch (error) {
      console.error(`${provider} login error:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Third-party provider login using authCode directly
  const loginWithThirdParty = async (
    provider: string,
    code: string,
    idToken?: string,
    redirectUri?: string
  ): Promise<void> => {
    setIsLoading(true);
    console.log(`${provider} login with auth code:`, { authCode: code, idToken, redirectUri });

    try {
      // 获取设备信息
      const deviceInfo = getDeviceInfo();

      // 使用提供的redirectUri，如果没有则使用当前页面路径
      const finalRedirectUri = redirectUri || `${window.location.origin}${window.location.pathname}`;

      // 确定认证方法
      let authMethod;
      if (provider === "google") {
        authMethod = AuthMethod.GoogleAuthCodeFlow;
      } else if (provider === "apple") {
        authMethod = AuthMethod.AppleAuthCodeFlow;
      } else {
        throw new Error(`${provider} login is not supported yet`);
      }

      // 构建授权信息
      const authorization: any = {
        code: code,
        redirectUri: finalRedirectUri
      };

      // 如果是 Apple 登录且提供了 idToken，则添加到授权信息中
      if (provider === "apple" && idToken) {
        authorization.idToken = idToken;
      }

      // 构建三方登录请求参数
      const thirdPartyLoginParams = {
        authMethod,
        identityScope: import.meta.env.VITE_APP_LOGIN_SCOPE || "player",
        authorization,
        bizType: ["AccountNotExistRegister", "Login"],
        device: deviceInfo
      };

      console.log('Third party login params:', thirdPartyLoginParams);

      // 调用三方登录API
      const loginResponse = await ucApi.auth.thirdPartyLogin(thirdPartyLoginParams);

      if (loginResponse.code !== 1000 || !loginResponse.data) {
        throw new Error(loginResponse.message || `${provider} login failed`);
      }

      // 登录成功，保存token
      if (loginResponse.data.token) {
        document.cookie = `anzir_token=Anzir ${loginResponse.data.token}; path=/; max-age=${loginResponse.data.expiresIn || 3600}`;
      }

      // 保存refreshToken到localStorage
      if (loginResponse.data.refreshToken) {
        localStorage.setItem("anzir_refresh_token", loginResponse.data.refreshToken);
      }

      // 先保存基本用户信息（如果有）
      let basicUserData: User | null = null;
      if (loginResponse.data.user) {
        basicUserData = {
          id: loginResponse.data.user.id || "",
          name: loginResponse.data.user.name || "",
          email: loginResponse.data.user.email || "",
          nickname: loginResponse.data.user.nickname || loginResponse.data.user.name || "",
          avatar: loginResponse.data.user.avatarUrl || import.meta.env.VITE_APP_BASE_HEAD,
          role: "user",
          provider: provider
        };

        // 暂时保存基本用户信息（后面会被详细信息覆盖）
        localStorage.setItem("anzir_user", JSON.stringify(basicUserData));
        setUser(basicUserData);
        setIsAuthenticated(true);
      }

      // 无论如何，在登录成功后都尝试获取用户详细信息
      try {
        console.log("Fetching user details after third-party login...");
        const userDetailResponse = await ucApi.auth.getUserDetail();
        console.log("User detail after third-party login:", userDetailResponse.data);

        if (userDetailResponse && userDetailResponse.data) {
          // 提取关键用户信息
          const userDetail = userDetailResponse.data;
          const userSubject = userDetail.details?.subject;

          // 构建用户对象，包含必要字段
          const userData: User = {
            id: userDetail.subjectIdentityId || "",
            name: userDetail.username || "",
            email: userDetail.details?.loginAccount?.email || "",
            nickname: userSubject?.nickname || userDetail.username || "",
            avatar: userSubject?.icon ? `${import.meta.env.VITE_STATIC_URL}${userSubject.icon}` : import.meta.env.VITE_APP_BASE_HEAD,
            role: "user",
            provider: provider,
            subjectIdentityId: userDetail.subjectIdentityId,
            identityId: userDetail.identityId,
            identityScope: userDetail.identityScope,
            details: userDetail.details
          };

          // 保存完整的用户信息到localStorage
          localStorage.setItem("anzir_user", JSON.stringify(userData));

          // 更新Context中的用户状态
          setUser(userData);
          setIsAuthenticated(true);
          console.log("User details updated successfully");
        } else {
          throw new Error("Failed to get user details: Empty response");
        }
      } catch (detailError) {
        console.error("Error fetching user details after third-party login:", detailError);
        
        // 如果之前没有保存基本用户信息，则显示错误
        if (!basicUserData) {
          throw new Error("Login successful but failed to get user details");
        } else {
          console.warn("Using basic user info because detailed info failed to load");
          // 基本信息已经设置，继续使用
        }
      }

      // 清除URL参数中的code
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);

      // 成功后处理重定向
      const redirectTo = sessionStorage.getItem('authRedirectTo') || '/profile';
      sessionStorage.removeItem('authRedirectTo');
      
      // 显示成功消息
      toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} Login Successful`, {
        description: 'You have successfully logged in.',
      });
      
      // 如果当前不是在回调页面上，则导航到重定向页面
      if (!window.location.pathname.includes('/auth/') && !window.location.pathname.includes('/callback')) {
        navigate(redirectTo, { replace: true });
      }
      
      return;
    } catch (error) {
      console.error(`${provider} login error:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password functionality
  const resetPassword = async (email: string): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await authService.resetPassword(email);
      if (response.error) {
        throw new Error(response.error);
      }
    } catch (error) {
      console.error("Password reset error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // 登出功能
  const logout = () => {
    intercomService.shutdown();
    clearAuthState();
    setUser(null);
    setIsAuthenticated(false);
    navigate("/");
  };

  // 检查用户最后一次注销信息
  const checkLastLogoff = async (email: string): Promise<ResponseData> => {
    try {
      return await ucApi.auth.lastLogoff({ email });
    } catch (error) {
      console.error("Error checking last logoff:", error);
      throw error;
    }
  };

  // 验证码验证
  const verifyCode = async (email: string, code: string, type: 'login' | 'register' | 'forget' | 'personal'): Promise<boolean> => {
    try {
      const response = await ucApi.auth.codeVerify({
        email,
        code,
        type
      });
      
      if (response.code !== 1000) {
        // 如果是验证码错误 (40001)
        if (response.code === 40001) {
          const error = new Error(response.message || "Verification code is incorrect");
          // @ts-ignore - 添加自定义code属性
          error.code = response.code;
          throw error;
        }
      }
      
      return response.code === 1000;
    } catch (error) {
      console.error("Code verification error:", error);
      throw error; // 将错误传递给调用方
    }
  };

  // 使用验证码重置密码
  const resetPasswordWithCode = async (email: string, code: string, password: string): Promise<boolean> => {
    try {
      // 假设后端有这样的API，如果没有需要添加
      const response = await ucApi.auth.resetPasswordWithCode({
        email,
        code,
        password: password
      });
      return response.code === 1000;
    } catch (error) {
      console.error("Reset password with code error:", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        setUser,
        login,
        loginWithOtp,
        loginWithCode,
        sendOtp,
        loginWithProvider,
        loginWithThirdParty,
        register,
        logout,
        resetPassword,
        resetPasswordWithCode,
        checkEmailOtpSettings,
        checkEmailExists,
        checkEmailLegality,
        checkLastLogoff,
        verifyCode
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
